import type { SVGProps } from 'react'

/**
 * Ícones da interface.
 *
 * Desenhados à mão em SVG em vez de vir de uma biblioteca: são poucos, e uma
 * dependência de ícones traz milhares que nunca serão usados. Todos seguem o
 * mesmo traço — contorno de 1,75, cantos e pontas arredondados, grade de 24 —
 * para não parecerem recortados de lugares diferentes.
 *
 * `currentColor` em tudo: a cor vem do texto ao lado, então o ícone acompanha
 * estado (ativo, desabilitado, perigo) sem precisar de variante própria.
 *
 * Nenhum deles carrega significado sozinho. Todo ícone aqui aparece ao lado do
 * rótulo escrito, ou com `title`/`aria-label` quando o espaço não permite —
 * ícone sem texto vira adivinhação, e a equipe muda.
 */

type Props = SVGProps<SVGSVGElement>

function Base({ children, ...props }: Props & { children: React.ReactNode }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...props}
    >
      {children}
    </svg>
  )
}

// ─────────────────────────── navegação ───────────────────────────

export const IconeBatePapo = (p: Props) => (
  <Base {...p}>
    <path d="M21 11.5a8.4 8.4 0 0 1-9 8.4 9 9 0 0 1-3.6-.7L3 21l1.9-4.9A8.4 8.4 0 0 1 12 3.1a8.4 8.4 0 0 1 9 8.4Z" />
  </Base>
)

export const IconeLeads = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="4" width="5" height="16" rx="1.5" />
    <rect x="10" y="4" width="5" height="11" rx="1.5" />
    <rect x="17" y="4" width="4" height="7" rx="1.5" />
  </Base>
)

export const IconeCalendario = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4" />
  </Base>
)

export const IconeEstatisticas = (p: Props) => (
  <Base {...p}>
    <path d="M4 20V10M10 20V4M16 20v-7M22 20H2" />
  </Base>
)

export const IconeConfiguracao = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="3" />
    <path d="M19.4 15a1.6 1.6 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.6 1.6 0 0 0-1.8-.3 1.6 1.6 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.6 1.6 0 0 0-1-1.5 1.6 1.6 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.6 1.6 0 0 0 .3-1.8 1.6 1.6 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.6 1.6 0 0 0 1.5-1 1.6 1.6 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.6 1.6 0 0 0 1.8.3H9a1.6 1.6 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.6 1.6 0 0 0 1 1.5 1.6 1.6 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.6 1.6 0 0 0-.3 1.8V9a1.6 1.6 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.6 1.6 0 0 0-1.5 1Z" />
  </Base>
)

export const IconeAnuncio = (p: Props) => (
  <Base {...p}>
    <path d="m3 11 18-5v12L3 14v-3Z" />
    <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
  </Base>
)

export const IconeSetaBaixo = (p: Props) => (
  <Base {...p}>
    <path d="m6 9 6 6 6-6" />
  </Base>
)

/** Três pontos: "há mais ações aqui dentro". */
export const IconeReticencias = (p: Props) => (
  <Base {...p}>
    <circle cx="5" cy="12" r="1.2" fill="currentColor" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" />
    <circle cx="19" cy="12" r="1.2" fill="currentColor" />
  </Base>
)

export const IconeInfo = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 16v-5M12 8h.01" />
  </Base>
)

// ─────────────────────────── ações ───────────────────────────

export const IconeEtiqueta = (p: Props) => (
  <Base {...p}>
    <path d="M20.6 13.4 12 22l-9-9V4a1 1 0 0 1 1-1h8.6Z" />
    <circle cx="7.5" cy="7.5" r="1.3" />
  </Base>
)

export const IconeMensagemAgendada = (p: Props) => (
  <Base {...p}>
    <path d="M21 12.5A8.4 8.4 0 0 1 8.4 19.2L3 21l1.9-4.9A8.4 8.4 0 1 1 21 12.5Z" />
    <path d="M12 8.5V12l2.2 1.3" />
  </Base>
)

export const IconeTarefa = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="5" width="18" height="16" rx="2" />
    <path d="M3 10h18M8 3v4M16 3v4M9 15l2 2 4-4" />
  </Base>
)

export const IconeRetorno = (p: Props) => (
  <Base {...p}>
    <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
    <path d="M3 3v5h5" />
  </Base>
)

export const IconeApoio = (p: Props) => (
  <Base {...p}>
    <path d="M16 20v-1.5a3.5 3.5 0 0 0-3.5-3.5h-5A3.5 3.5 0 0 0 4 18.5V20" />
    <circle cx="10" cy="8" r="3.2" />
    <path d="M20 20v-1.5a3.5 3.5 0 0 0-2.6-3.4M15.5 5.2a3.2 3.2 0 0 1 0 5.6" />
  </Base>
)

export const IconeTransferir = (p: Props) => (
  <Base {...p}>
    <path d="M4 8h13M14 5l3 3-3 3M20 16H7M10 13l-3 3 3 3" />
  </Base>
)

/**
 * Telefone cortado: encerrar a conversa.
 *
 * Era um X dentro de um círculo, que é o desenho de "cancelar" e servia para
 * qualquer coisa. Encerrar chamado é desligar — e o gesto de desligar já tem
 * um desenho que todo mundo conhece.
 */
export const IconeEncerrar = (p: Props) => (
  <Base {...p}>
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.42 19.42 0 0 1-3.33-2.67m-2.67-3.34a19.79 19.79 0 0 1-3.07-8.63A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91" />
    <path d="M22 2 2 22" />
  </Base>
)

export const IconeReabrir = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 7v5l3 2" />
  </Base>
)

export const IconeNegociacao = (p: Props) => (
  <Base {...p}>
    <path d="M3 17V7a2 2 0 0 1 2-2h6l2 2h6a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />
    <path d="M12 10v5M9.5 12.5h5" />
  </Base>
)

export const IconeFiltro = (p: Props) => (
  <Base {...p}>
    <path d="M3 5h18l-7 8v6l-4 2v-8Z" />
  </Base>
)

export const IconeBuscar = (p: Props) => (
  <Base {...p}>
    <circle cx="11" cy="11" r="6.5" />
    <path d="m16 16 4.5 4.5" />
  </Base>
)

export const IconeX = (p: Props) => (
  <Base {...p}>
    <path d="M6 6l12 12M18 6 6 18" />
  </Base>
)

export const IconeMais = (p: Props) => (
  <Base {...p}>
    <path d="M12 5v14M5 12h14" />
  </Base>
)

export const IconeAnexo = (p: Props) => (
  <Base {...p}>
    <path d="M21 12.5 12.5 21a5.5 5.5 0 0 1-7.8-7.8l8.5-8.5a3.7 3.7 0 0 1 5.2 5.2l-8.5 8.5a1.8 1.8 0 0 1-2.6-2.6l7.9-7.8" />
  </Base>
)

export const IconeEmoji = (p: Props) => (
  <Base {...p}>
    <circle cx="12" cy="12" r="9" />
    <path d="M8.5 14.5a4.5 4.5 0 0 0 7 0" />
    <path d="M9 9.5h.01M15 9.5h.01" strokeWidth={2.5} />
  </Base>
)

/** Balão de comentário: a nota que fica na conversa e não sai dela. */
export const IconeNotaInterna = (p: Props) => (
  <Base {...p}>
    <path d="M20 15.5a2.5 2.5 0 0 1-2.5 2.5H8l-4 3V6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5Z" />
  </Base>
)

export const IconeImagem = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <circle cx="8.5" cy="9.5" r="1.5" />
    <path d="m3.5 17 4.6-4.6a2 2 0 0 1 2.8 0L16 17.5m1.5-2 1.2-1.2a2 2 0 0 1 2.8 0l.5.5" />
  </Base>
)

export const IconeArquivo = (p: Props) => (
  <Base {...p}>
    <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
    <path d="M14 3v5h5" />
  </Base>
)

/**
 * O símbolo do WhatsApp, preenchido e não de contorno.
 *
 * É marca registrada e tem forma própria — desenhá-lo com o traço dos outros
 * ícones o deixaria irreconhecível, que é o oposto do que uma marca serve.
 */
/** Lápis: editar no lugar. */
export const IconeLapis = (p: Props) => (
  <Base {...p}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </Base>
)

/** Seta voltando: responder citando a mensagem, como no WhatsApp. */
export const IconeResponder = (p: Props) => (
  <Base {...p}>
    <path d="M9 14 4 9l5-5" />
    <path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5V20" />
  </Base>
)

/** Duas folhas: copiar o texto. */
export const IconeCopiar = (p: Props) => (
  <Base {...p}>
    <rect x="9" y="9" width="12" height="12" rx="2" />
    <path d="M5 15H4a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v1" />
  </Base>
)

export const IconeWhatsApp = (p: Props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" focusable="false" {...p}>
    <path d="M17.47 14.38c-.3-.15-1.75-.86-2.02-.96-.27-.1-.47-.15-.67.15-.2.3-.77.96-.94 1.16-.17.2-.35.22-.64.07-.3-.15-1.25-.46-2.38-1.47-.88-.78-1.47-1.75-1.64-2.05-.17-.3-.02-.46.13-.6.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.08-.15-.67-1.6-.92-2.2-.24-.58-.49-.5-.67-.5h-.57c-.2 0-.52.07-.79.37-.27.3-1.04 1.01-1.04 2.47s1.06 2.86 1.21 3.06c.15.2 2.1 3.2 5.08 4.49.71.3 1.26.49 1.69.63.71.22 1.36.19 1.87.12.57-.09 1.75-.72 2-1.41.25-.69.25-1.28.17-1.41-.07-.13-.27-.2-.57-.35M12.05 21.5h-.02a9.5 9.5 0 0 1-4.83-1.32l-.35-.2-3.59.94.96-3.5-.23-.36a9.44 9.44 0 0 1-1.45-5.05c0-5.22 4.26-9.47 9.5-9.47a9.44 9.44 0 0 1 9.49 9.48c0 5.22-4.26 9.47-9.48 9.47M20.52 3.5A11.87 11.87 0 0 0 12.05 0C5.5 0 .16 5.33.16 11.89c0 2.1.55 4.15 1.6 5.96L.06 24l6.3-1.65a11.86 11.86 0 0 0 5.68 1.45h.01c6.55 0 11.89-5.34 11.89-11.9 0-3.18-1.24-6.17-3.49-8.42" />
  </svg>
)

/** Prédio com divisões: o setor que atende. */
export const IconeSetor = (p: Props) => (
  <Base {...p}>
    <path d="M3 21h18M5 21V7l7-4 7 4v14M9 21v-4h6v4" />
  </Base>
)

export const IconeMicrofone = (p: Props) => (
  <Base {...p}>
    <rect x="9" y="2" width="6" height="11" rx="3" />
    <path d="M5 10a7 7 0 0 0 14 0M12 17v4M9 21h6" />
  </Base>
)

/** Quadrado com seta para dentro: parar a gravação. */
export const IconeParar = (p: Props) => (
  <Base {...p}>
    <rect x="6" y="6" width="12" height="12" rx="2" />
  </Base>
)

export const IconeEnviar = (p: Props) => (
  <Base {...p}>
    <path d="M21 3 10.5 13.5M21 3l-6.8 18-3.7-7.5L3 9.8Z" />
  </Base>
)

export const IconeRespostaRapida = (p: Props) => (
  <Base {...p}>
    <path d="M13 2 4.5 13H11l-1 9 8.5-11H12Z" />
  </Base>
)

export const IconeBaixar = (p: Props) => (
  <Base {...p}>
    <path d="M12 3v11M8 10.5l4 4 4-4" />
    <path d="M4 20h16" />
  </Base>
)

/** Triângulo de tocar, para miniatura de vídeo. */
export const IconeTocar = (p: Props) => (
  <Base {...p}>
    <path d="M8 5.5v13l10.5-6.5z" fill="currentColor" />
  </Base>
)

/** Um prédio: a empresa, onde o contato trabalha. */
export const IconeEmpresa = (p: Props) => (
  <Base {...p}>
    <path d="M4 21V5a1 1 0 0 1 1-1h9a1 1 0 0 1 1 1v16" />
    <path d="M15 9h4a1 1 0 0 1 1 1v11" />
    <path d="M3 21h18M8 8h3M8 12h3M8 16h3" />
  </Base>
)

/** Os contatos: duas pessoas lado a lado. */
export const IconeContatos = (p: Props) => (
  <Base {...p}>
    <circle cx="9" cy="8" r="3.25" />
    <path d="M3.5 19.5a5.5 5.5 0 0 1 11 0" />
    <path d="M16 5.2a3.25 3.25 0 0 1 0 5.6M17.5 14.2a5.5 5.5 0 0 1 3 5.3" />
  </Base>
)

/** Painel do admin: um quadro com uma marca de conferido. */
export const IconePainel = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="4" width="18" height="16" rx="2.5" />
    <path d="M3 9h18" />
    <path d="m8 14 2.5 2.5L16 11" />
  </Base>
)

export const IconePasta = (p: Props) => (
  <Base {...p}>
    <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
  </Base>
)

export const IconeVideo = (p: Props) => (
  <Base {...p}>
    <rect x="3" y="6" width="13" height="12" rx="2" />
    <path d="M16 10.5 21 8v8l-5-2.5z" />
  </Base>
)

export const IconeLixeira = (p: Props) => (
  <Base {...p}>
    <path d="M4 7h16M10 7V5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1v2" />
    <path d="M6 7l1 12a2 2 0 0 0 2 2h6a2 2 0 0 0 2-2l1-12" />
    <path d="M10 11v6M14 11v6" />
  </Base>
)

export const IconeImprimir = (p: Props) => (
  <Base {...p}>
    <path d="M7 9V3h10v6M7 19H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2" />
    <rect x="7" y="15" width="10" height="6" rx="1" />
  </Base>
)

export const IconeSino = (p: Props) => (
  <Base {...p}>
    <path d="M18 8.5a6 6 0 1 0-12 0c0 6-2.5 7.5-2.5 7.5h17S18 14.5 18 8.5Z" />
    <path d="M13.7 20a2 2 0 0 1-3.4 0" />
  </Base>
)

/** Alto-falante com ondas: o som das mensagens (ADR 0040). */
export const IconeSom = (p: Props) => (
  <Base {...p}>
    <path d="M11 5 6 9H3v6h3l5 4z" />
    <path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13" />
  </Base>
)

/** Lua crescente: o modo escuro (ADR 0040). */
export const IconeLua = (p: Props) => (
  <Base {...p}>
    <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
  </Base>
)


export const IconeSair = (p: Props) => (
  <Base {...p}>
    <path d="M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3" />
    <path d="M16 17l5-5-5-5M21 12H9" />
  </Base>
)

/** Modelo aprovado pela Meta (ADR 0033): a folha com o visto. */
export const IconeCerto = (p: Props) => (
  <Base {...p}>
    <path d="m5 12.5 4.5 4.5L19 7.5" />
  </Base>
)

export const IconeModeloAprovado = (p: Props) => (
  <Base {...p}>
    <path d="M7 3h7l5 5v12a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
    <path d="M14 3v5h5" />
    <path d="m9.5 14.5 2 2 3.5-4" />
  </Base>
)

// ─────────────────────────── tipos de tarefa ───────────────────────────

/** Ligação: o fone do telefone. */
export const IconeTelefone = (p: Props) => (
  <Base {...p}>
    <path d="M21 16.9v3a2 2 0 0 1-2.2 2 19.8 19.8 0 0 1-8.6-3.1 19.5 19.5 0 0 1-6-6A19.8 19.8 0 0 1 1.1 4.2 2 2 0 0 1 3.1 2h3a2 2 0 0 1 2 1.7c.1 1 .4 1.9.7 2.8a2 2 0 0 1-.5 2.1L7 9.9a16 16 0 0 0 6 6l1.3-1.3a2 2 0 0 1 2.1-.4c.9.3 1.8.6 2.8.7a2 2 0 0 1 1.7 2Z" />
  </Base>
)

/**
 * Reunião: a câmera do Google Meet, nas cores dela.
 *
 * É o único ícone colorido do arquivo, de propósito: pedido do Mike, a
 * reunião da equipe é pelo Meet, e uma câmera cinza não diz "Meet" a
 * ninguém. Por isso ele não segue `currentColor`; em tarefa concluída quem
 * usa apaga a cor por fora (grayscale).
 */
export const IconeGoogleMeet = (p: Props) => (
  <svg viewBox="0 0 87.5 72" aria-hidden="true" focusable="false" {...p}>
    <path fill="#00832d" d="M49.5 36l8.53 9.75 11.47 7.33 2-17.02-2-16.64-11.69 6.44z" />
    <path fill="#0066da" d="M0 51.5V66c0 3.315 2.685 6 6 6h14.5l3-10.96-3-9.54-9.95-3z" />
    <path fill="#e94235" d="M20.5 0L0 20.5l10.55 3 9.95-3 2.95-9.41z" />
    <path fill="#2684fc" d="M20.5 20.5H0v31h20.5z" />
    <path
      fill="#00ac47"
      d="M82.6 8.68L69.5 19.42v33.66l13.16 10.79c1.97 1.54 4.85.135 4.85-2.37V11c0-2.535-2.945-3.925-4.91-2.32zM49.5 36v15.5h-29V72h43c3.315 0 6-2.685 6-6V53.08z"
    />
    <path fill="#ffba00" d="M63.5 0h-43v20.5h29V36l20-16.57V6c0-3.315-2.685-6-6-6z" />
  </svg>
)
